import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Plus, LogOut, KeyRound, Edit3, CheckCircle2, Ban, RotateCcw, X, Save, Loader2, MapPin, ChevronDown, ChevronUp, Phone, Mail, Link2, Copy, CalendarRange, UserPen, MessageSquareText, Search, ReceiptText, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { authApi, getToken } from "@/lib/auth";
import { smsHref, firstName } from "@/lib/sms";
import Seo from "@/components/Seo";
import ContentEditor from "@/components/ContentEditor";
import CalendarSync from "@/components/admin/CalendarSync";
import BookingCalendar from "@/components/admin/BookingCalendar";
import AdminReviews from "@/components/admin/AdminReviews";
import AdminRewards from "@/components/admin/AdminRewards";
import AdminPitchMedia from "@/components/admin/AdminPitchMedia";
import AdminPitchMap from "@/components/admin/AdminPitchMap";
import AdminArrivals from "@/components/admin/AdminArrivals";
import AdminOnSite from "@/components/admin/AdminOnSite";
import AdminCheckIns from "@/components/admin/AdminCheckIns";
import MoveBookingDialog from "@/components/admin/MoveBookingDialog";
import AdminCustomers from "@/components/admin/AdminCustomers";
import AdminAnalytics from "@/components/admin/AdminAnalytics";
import AdminAccounts from "@/components/admin/AdminAccounts";
import AdminAssistant from "@/components/admin/AdminAssistant";
import AdminEnquiries from "@/components/admin/AdminEnquiries";
import { InstallCard, NotificationsCard, PasskeyCard } from "@/components/admin/PwaCards";
import { VoiceBriefCard, MorningBriefChip } from "@/components/admin/VoiceBriefCards";
import { startBookingPoll, stopBookingPoll } from "@/lib/pwa";
import { CalendarFeedCard, DailyBriefCard } from "@/components/admin/AdminNotifyCards";
import { VoiceNoteRecorder } from "@/components/admin/VoiceNoteRecorder";

// Static roster of pitches/lodges — mirrors backend UNIT_TYPES config for grid rendering.
const PITCH_GROUPS = [
  {
    key: "daisy-hs",
    label: "Daisy Lake Hardstanding",
    units: Array.from({ length: 10 }, (_, i) => ({
      unit_type: "touring",
      unit_id: `daisy-hs-${String(i + 1).padStart(2, "0")}`,
      short: `${i + 1}`,
    })),
  },
  {
    key: "walnut-hs",
    label: "Walnut Lake Hardstanding",
    units: Array.from({ length: 5 }, (_, i) => ({
      unit_type: "touring",
      unit_id: `walnut-hs-${i + 11}`,
      short: `${i + 11}`,
    })),
  },
  {
    key: "walnut-grass",
    label: "Walnut Lake Grass",
    units: Array.from({ length: 12 }, (_, i) => ({
      unit_type: "touring",
      unit_id: `walnut-grass-${String(i + 1).padStart(2, "0")}`,
      short: `${i + 1}`,
    })),
  },
  {
    key: "walnut-tent",
    label: "Walnut Lake Tent",
    units: Array.from({ length: 20 }, (_, i) => ({
      unit_type: "touring",
      unit_id: `walnut-tent-${String(i + 1).padStart(2, "0")}`,
      short: `${i + 1}`,
    })),
  },
  {
    key: "lodges",
    label: "Lodges",
    units: [
      { unit_type: "lodge", unit_id: "lodge-heron", short: "Heron" },
      { unit_type: "lodge", unit_id: "lodge-alpine", short: "Alpine" },
      { unit_type: "lodge", unit_id: "lodge-kingfisher", short: "Kingfisher" },
    ],
  },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _buildSlots = (startH, endH) => {
  const out = [];
  for (let h = startH; h <= endH; h++) {
    out.push(`${String(h).padStart(2, "0")}:00`);
    if (h < endH) out.push(`${String(h).padStart(2, "0")}:30`);
  }
  return out;
};
const TIME_SLOTS_CHECKIN_TOURING = _buildSlots(13, 20); // pitches 1PM–8PM
const TIME_SLOTS_CHECKIN_LODGE = _buildSlots(14, 20); // lodges 2PM–8PM

const gbp = (v) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);

const TABS = [
  { id: "bookings", label: "Bookings" },
  { id: "enquiries", label: "Enquiries" },
  { id: "analytics", label: "Analytics" },
  { id: "accounts", label: "Accounts" },
  { id: "arrivals", label: "Arrivals" },
  { id: "onsite", label: "On-Site" },
  { id: "checkins", label: "Check-ins" },
  { id: "customers", label: "Customers" },
  { id: "assistant", label: "Assistant" },
  { id: "blocks", label: "Availability" },
  { id: "calendar", label: "Calendar Sync" },
  { id: "reviews", label: "Reviews" },
  { id: "rewards", label: "Rewards" },
  { id: "pitch-media", label: "Pitch Media" },
  { id: "content", label: "Content" },
  { id: "settings", label: "Settings" },
];

/** Fulfil a guest's specific-pitch request on a pooled camping booking. */
const PitchAssign = ({ booking, onDone }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  const openPicker = async () => {
    setOpen(true);
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/availability`, {
        params: { unit_type: "touring", start_date: booking.start_date, end_date: booking.end_date },
      });
      const free = (data.units || []).filter((u) => u.available);
      setOptions(free);
      setPick(free[0]?.unit_id || "");
    } catch {
      toast.error("Couldn't load pitch availability.");
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const assign = async () => {
    if (!pick || busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${booking.id}/assign-pitch`, { unit_id: pick });
      toast.success(`Reserved ${data.pitch_name} for ${booking.guest_name || "guest"}.`);
      onDone?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't assign that pitch.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={openPicker}
        data-testid={`assign-pitch-open-${booking.id}`}
        className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-full border border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white text-[9px] uppercase tracking-[0.12em] font-semibold transition"
      >
        <MapPin size={10} /> Assign pitch
      </button>
    );
  }
  return (
    <div className="mt-1.5 space-y-1.5 max-w-[220px]">
      {busy && !options ? (
        <span className="text-[10px] text-[#6A5F58]">Checking availability…</span>
      ) : (
        <>
          <select
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            data-testid={`assign-pitch-select-${booking.id}`}
            className="w-full text-[11px] h-8 rounded-md border border-[#DDD1BE] bg-[#F5EFE6] px-2"
          >
            {(options || []).map((u) => (
              <option key={u.unit_id} value={u.unit_id}>{u.unit_name}</option>
            ))}
          </select>
          {options && options.length === 0 && (
            <p className="text-[10px] text-[#8C4E58]">No pitches free for these dates.</p>
          )}
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={assign}
              disabled={!pick || busy}
              data-testid={`assign-pitch-save-${booking.id}`}
              className="px-2.5 py-1 rounded-full bg-[#0F0E0E] text-[#F5EFE6] text-[9px] uppercase tracking-[0.12em] font-semibold disabled:opacity-50"
            >
              {busy ? "…" : "Reserve"}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-2.5 py-1 rounded-full border border-[#DDD1BE] text-[#6A5F58] text-[9px] uppercase tracking-[0.12em]"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const AdminPage = () => {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState(() => {
    // Support deep links from the PWA manifest shortcuts: /admin?tab=assistant
    try {
      const p = new URLSearchParams(window.location.search).get("tab");
      if (p && TABS.some((t) => t.id === p)) return p;
    } catch { /* ignore */ }
    return "bookings";
  });
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [units, setUnits] = useState([]);
  const [form, setForm] = useState({
    unit_type: "lodge",
    unit_id: "",
    start_date: "",
    end_date: "",
    reason: "",
  });
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  // Freshly generated payment link shown in a copy dialog (for text/WhatsApp).
  const [payLink, setPayLink] = useState(null);
  // Reservations date-range search (bookings + manual blocks in a period).
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  // Reservations source tabs (Website = direct: website checkout, manual,
  // Daisy, voice) + single-day quick filter.
  const [bookingSourceTab, setBookingSourceTab] = useState("website"); // "website" | "pitchup" | "bookingcom"
  const [dayFilter, setDayFilter] = useState("");
  // Free-text customer search — covers every source tab at once (name, email,
  // phone, booking ref, vehicle reg, unit, dates) so a guest is never lost.
  const [search, setSearch] = useState("");

  const sourceMatch = (b, tab) => {
    const s = b.source || "";
    if (tab === "pitchup") return s === "external:pitchup";
    if (tab === "bookingcom") return s === "external:booking";
    return !s.startsWith("external:");
  };
  const tabCounts = {
    website: bookings.filter((b) => sourceMatch(b, "website")).length,
    pitchup: bookings.filter((b) => sourceMatch(b, "pitchup")).length,
    bookingcom: bookings.filter((b) => sourceMatch(b, "bookingcom")).length,
  };

  // Reservations range/day search — inclusive date overlap. Latest booking on
  // top: created_at desc; channel bookings all share their last-sync timestamp,
  // so ties fall back to soonest stay first.
  const rangeActive = Boolean(rangeFrom && rangeTo && rangeFrom <= rangeTo);
  const dayActive = Boolean(dayFilter) && !rangeActive;
  const tabBookings = bookings.filter((b) => sourceMatch(b, bookingSourceTab));
  const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const searching = searchWords.length > 0;
  const searchHaystack = (b) =>
    [b.guest_name, b.guest_email, b.guest_phone, b.booking_id, b.id, b.vehicle_reg, b.unit_type, b.unit_id, b.start_date, b.end_date]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
  const visibleBookings = (searching
    ? bookings.filter((b) => searchWords.every((w) => searchHaystack(b).includes(w)))
    : rangeActive
    ? tabBookings.filter((b) => b.start_date <= rangeTo && b.end_date >= rangeFrom)
    : dayActive
      ? tabBookings.filter((b) => b.start_date <= dayFilter && b.end_date >= dayFilter)
      : tabBookings
  ).slice().sort((a, b) =>
    String(b.created_at || "").localeCompare(String(a.created_at || "")) ||
    String(a.start_date || "").localeCompare(String(b.start_date || ""))
  );
  const dayCounts = dayActive
    ? {
        arriving: visibleBookings.filter((b) => b.start_date === dayFilter).length,
        departing: visibleBookings.filter((b) => b.end_date === dayFilter).length,
        staying: visibleBookings.filter((b) => b.start_date < dayFilter && b.end_date > dayFilter).length,
      }
    : null;
  const rangeBlocks = rangeActive
    ? blocks.filter((bl) => bl.start_date <= rangeTo && bl.end_date >= rangeFrom)
    : [];
  const rangeCounts = rangeActive
    ? {
        paid: visibleBookings.filter((b) => b.payment_status === "paid").length,
        pending: visibleBookings.filter((b) => b.payment_status === "pending").length,
        channel: visibleBookings.filter((b) => (b.source || "").startsWith("external:")).length,
      }
    : null;
  const rangeUnitName = (type, id) => {
    const grp = units.find((u) => u.type === type);
    if (!id) return `All ${grp?.label || type}`;
    return grp?.units.find((x) => x.id === id)?.name || id;
  };

  // Auth guard — redirect to /admin/login if no token or /me fails
  useEffect(() => {
    if (!getToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    authApi
      .me()
      .then(setMe)
      .catch(() => nav("/admin/login", { replace: true }));
  }, [nav]);

  const loadAll = async () => {
    try {
      const [b, bl, u] = await Promise.all([
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/blocks`),
        axios.get(`${API}/units`),
      ]);
      setBookings(b.data || []);
      setBlocks(bl.data || []);
      setUnits(u.data.types || []);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error("Failed to load admin data.");
    }
  };

  useEffect(() => {
    if (me) loadAll();
  }, [me]);

  // Start / stop the PWA booking notification poll while admin is signed in.
  useEffect(() => {
    if (!me) return undefined;
    startBookingPoll(() => getToken(), process.env.REACT_APP_BACKEND_URL);
    return () => stopBookingPoll();
  }, [me]);

  const currentType = units.find((u) => u.type === form.unit_type);

  // Website checkouts that never completed payment — they still show in the main
  // list below (every booking shows, whatever its state); this section is just a
  // quick rescue call-list with their contact details and a resend button.
  const isIncompletePayment = (b) =>
    !!b.square_order_id &&
    (b.payment_status === "pending" ||
      (b.payment_status === "expired" && b.expired_reason === "abandoned_checkout"));
  const incompleteBookings = bookings.filter(isIncompletePayment);

  // Cancelled bookings — always visible in their own colour-coded section so a
  // cancellation (and its refund) is never lost. Latest cancellation first.
  const cancelledBookings = bookings
    .filter((b) => b.payment_status === "cancelled")
    .sort((a, b2) => String(b2.cancelled_at || "").localeCompare(String(a.cancelled_at || "")));
  const refundsDueTotal = cancelledBookings.reduce(
    (n, b) => n + Math.max(0, (b.cancel_refund_amount || 0) - (b.total_refunded || 0)),
    0
  );

  // Activity board — traffic-light sections above the list so the office spots
  // anything a customer did without scrolling the whole table.
  // GREEN: new bookings in the last 7 days (any channel). AMBER: bookings the
  // guest changed online. RED: cancelled (handled below).
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const newBookings = bookings
    .filter((b) => {
      if (["cancelled", "expired", "failed"].includes(b.payment_status)) return false;
      const t = new Date(b.created_at || 0).getTime();
      return t && t >= sevenDaysAgo;
    })
    .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
  const modifiedBookings = bookings
    .filter((b) => b.modified_at && !["cancelled", "expired", "failed"].includes(b.payment_status))
    .sort((a, b) => String(b.modified_at || "").localeCompare(String(a.modified_at || "")));

  const resendPaymentLink = async (b) => {
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${b.id}/payment-link`);
      setPayLink({
        url: data.checkout_url,
        emailed: data.emailed,
        amount: data.amount,
        email: b.guest_email,
        guest: b.guest_name,
        phone: b.guest_phone,
        revived: data.revived,
      });
      if (data.revived) {
        toast.success("Booking revived to pending — the unit is held again for 60 minutes.");
      }
      loadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send the payment link.");
    }
  };

  const copyPayLink = async () => {
    if (!payLink?.url) return;
    try {
      await navigator.clipboard.writeText(payLink.url);
      toast.success("Link copied — paste it into a text or WhatsApp.");
    } catch {
      toast.error("Couldn't copy — select the link and copy it manually.");
    }
  };

  // Channel bookings (Booking.com/Pitchup/Airbnb) arrive with no guest
  // details — Justina attaches name/phone/email after looking them up in the
  // channel extranet. Marked manual server-side so the re-sync preserves them.
  const [guestEdit, setGuestEdit] = useState(null);
  const [guestForm, setGuestForm] = useState({ name: "", phone: "", email: "" });
  const openGuestEdit = (b) => {
    setGuestEdit(b.id);
    setGuestForm({ name: b.guest_name || "", phone: b.guest_phone || "", email: b.guest_email || "" });
  };
  const saveGuestDetails = async (b) => {
    try {
      await axios.post(`${API}/admin/bookings/${b.id}/channel-details`, {
        guest_name: guestForm.name,
        guest_phone: guestForm.phone,
        guest_email: guestForm.email,
      });
      toast.success("Guest details saved — they'll survive the calendar re-sync.");
      setGuestEdit(null);
      loadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't save guest details.");
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    return `${Math.floor(hrs / 24)} day${Math.floor(hrs / 24) > 1 ? "s" : ""} ago`;
  };

  const incompleteUnitLabel = (b) => {
    if (b.unit_type === "lodge") {
      return units.find((u) => u.type === "lodge")?.units?.find((u) => u.id === b.unit_id)?.name || "Lodge";
    }
    return b.sub_type ? `${b.sub_type} pitch` : "Camping pitch";
  };

  const createBlock = async (e) => {
    e.preventDefault();
    if (!form.unit_id || !form.start_date || !form.end_date) {
      toast.error("Pick a unit and dates.");
      return;
    }
    try {
      await axios.post(`${API}/admin/blocks`, form);
      toast.success("Block added.");
      setForm({ ...form, start_date: "", end_date: "", reason: "" });
      loadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not add block.");
    }
  };

  const removeBlock = async (id) => {
    try {
      await axios.delete(`${API}/admin/blocks/${id}`);
      toast.success("Block removed.");
      loadAll();
    } catch {
      toast.error("Could not remove block.");
    }
  };

  // Bulk block state --------------------------------------------------------
  const [bulkForm, setBulkForm] = useState({
    category: "hardstanding",
    start_date: "",
    end_date: "",
    reason: "Owner block",
  });
  const [bulkBusy, setBulkBusy] = useState(false);

  // ---- Multi-select individual pitches -----------------------------------
  const [multiSel, setMultiSel] = useState({}); // { "unit_type:unit_id": true }
  const [multiForm, setMultiForm] = useState({
    start_date: "",
    end_date: "",
    reason: "Owner block",
  });
  const [multiNote, setMultiNote] = useState(null); // { audio_url, transcript }
  const [multiBusy, setMultiBusy] = useState(false);

  const toggleMulti = (unit_type, unit_id) => {
    const key = `${unit_type}:${unit_id}`;
    setMultiSel((s) => ({ ...s, [key]: !s[key] }));
  };
  const setMultiGroup = (units, mode = "toggle") => {
    setMultiSel((s) => {
      const next = { ...s };
      const anyOn = units.some((u) => next[`${u.unit_type}:${u.unit_id}`]);
      const setTo = mode === "on" ? true : mode === "off" ? false : !anyOn;
      for (const u of units) next[`${u.unit_type}:${u.unit_id}`] = setTo;
      return next;
    });
  };
  const clearMulti = () => setMultiSel({});
  const selectedMultiUnits = Object.entries(multiSel)
    .filter(([, v]) => v)
    .map(([k]) => {
      const [unit_type, unit_id] = k.split(":");
      return { unit_type, unit_id };
    });

  const submitMulti = async (mode) => {
    if (selectedMultiUnits.length === 0) {
      toast.error("Select at least one pitch or lodge.");
      return;
    }
    if (!multiForm.start_date || !multiForm.end_date) {
      toast.error("Pick a date range first.");
      return;
    }
    if (mode === "block" && !confirm(`Block ${selectedMultiUnits.length} selected unit(s) from ${multiForm.start_date} to ${multiForm.end_date}?`)) return;
    if (mode === "clear" && !confirm(`Clear blocks on ${selectedMultiUnits.length} selected unit(s) overlapping ${multiForm.start_date} → ${multiForm.end_date}?`)) return;
    setMultiBusy(true);
    try {
      const endpoint = mode === "block" ? "/admin/blocks/multi" : "/admin/blocks/multi/clear";
      const body =
        mode === "block"
          ? {
              units: selectedMultiUnits,
              ...multiForm,
              admin_note: multiNote?.transcript,
              admin_note_audio_url: multiNote?.audio_url,
            }
          : { units: selectedMultiUnits, start_date: multiForm.start_date, end_date: multiForm.end_date };
      const { data } = await axios.post(`${API}${endpoint}`, body);
      if (mode === "block") toast.success(`${data.created} blocked · ${data.skipped} already existed.`);
      else toast.success(`${data.deleted} block${data.deleted === 1 ? "" : "s"} cleared.`);
      loadAll();
      // Reset the note but keep the selection so admin can re-use it
      setMultiNote(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Action failed.");
    } finally {
      setMultiBusy(false);
    }
  };

  const submitBulk = async (mode) => {
    if (!bulkForm.start_date || !bulkForm.end_date) {
      toast.error("Pick a date range first.");
      return;
    }
    const label = { hardstanding: "hardstanding pitches", grass: "grass pitches", tent: "tent pitches", lodge: "lodges" }[bulkForm.category];
    if (mode === "block") {
      if (!confirm(`Block all ${label} from ${bulkForm.start_date} to ${bulkForm.end_date}?`)) return;
    } else {
      if (!confirm(`Clear all blocks on ${label} that overlap ${bulkForm.start_date} → ${bulkForm.end_date}? (This will free the dates for new bookings.)`)) return;
    }
    setBulkBusy(true);
    try {
      const endpoint = mode === "block" ? "/admin/blocks/bulk" : "/admin/blocks/bulk/clear";
      const { data } = await axios.post(`${API}${endpoint}`, bulkForm);
      if (mode === "block") {
        toast.success(`${data.created} ${label} blocked · ${data.skipped} already existed.`);
      } else {
        toast.success(`${data.deleted} block${data.deleted === 1 ? "" : "s"} cleared across ${data.total_units} ${label}.`);
      }
      loadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Bulk action failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  // Booking admin actions --------------------------------------------------
  const [editing, setEditing] = useState(null); // booking being modified inline
  const [editForm, setEditForm] = useState({});
  const [refunding, setRefunding] = useState(null); // booking id being refunded
  const [showManualForm, setShowManualForm] = useState(false);
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [showNew, setShowNew] = useState(true);       // green — fresh bookings, open by default
  const [showModified, setShowModified] = useState(true); // amber — guest changes, open by default
  const [showAllBookings, setShowAllBookings] = useState(true); // the full list — retractable too
  // An active search/date filter must always reveal the list, even if retracted.
  const allBookingsOpen = showAllBookings || searching || rangeActive || dayActive;
  // Money outstanding on a cancellation is the one thing that must never be
  // missed — the red section springs open whenever a refund is due.
  useEffect(() => {
    if (refundsDueTotal > 0) setShowCancelled(true);
  }, [refundsDueTotal]);
  // Two-step fail-safe for destructive booking actions (delete / cancel /
  // refund) — a mistake must never go straight through.
  const [danger, setDanger] = useState(null); // {kind, booking, step, reason?, refundAmount?, amount?}
  const [manualForm, setManualForm] = useState({
    unit_type: "lodge",
    unit_id: "",
    start_date: "",
    end_date: "",
    guests: 2,
    dogs: 0,
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    payment_status: "paid",
    notes: "",
  });

  const submitManual = async (e) => {
    e.preventDefault();
    if (!manualForm.unit_id || !manualForm.start_date || !manualForm.end_date || !manualForm.guest_name || !manualForm.guest_email) {
      toast.error("Fill in unit, dates, name and email.");
      return;
    }
    try {
      await axios.post(`${API}/admin/bookings`, manualForm);
      toast.success("Booking created.");
      setShowManualForm(false);
      setManualForm({ ...manualForm, unit_id: "", start_date: "", end_date: "", guest_name: "", guest_email: "", guest_phone: "", notes: "" });
      loadAll();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create booking.");
    }
  };

  const startEdit = (b) => {
    setEditing(b.id);
    setEditForm({
      start_date: b.start_date,
      end_date: b.end_date,
      guests: b.guests || 2,
      dogs: b.dogs || 0,
      unit_id: b.unit_id,
    });
  };

  const saveEdit = async (b) => {
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${b.id}/modify`, editForm);
      toast.success(
        data.difference > 0
          ? `Updated. £${data.difference.toFixed(2)} extra owed on arrival.`
          : data.difference < 0
          ? `Updated. £${(-data.difference).toFixed(2)} refund due.`
          : "Booking updated."
      );
      setEditing(null);
      loadAll();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update.");
    }
  };

  const markPaid = async (b) => {
    if (!window.confirm(`Mark ${b.guest_name || b.guest_email}'s booking as PAID? This will fire confirmation emails.`)) return;
    try {
      await axios.post(`${API}/admin/bookings/${b.id}/mark-paid`, {});
      toast.success("Marked as paid. Confirmation emails sent.");
      loadAll();
    } catch {
      toast.error("Could not mark paid.");
    }
  };

  // Make an expired/failed booking valid again — paid (money seen in Square) or re-opened unpaid.
  const reactivateBooking = async (b, mode) => {
    const msg = mode === "paid"
      ? `Make ${b.guest_name || b.guest_email}'s booking VALID and mark it PAID?\n\nUse this when the payment is showing in Square (or was taken another way) but the booking expired.`
      : `Re-open ${b.guest_name || b.guest_email}'s booking as AWAITING PAYMENT?\n\nThe unit is held again for a fresh 60-minute window — send a payment link afterwards if needed.`;
    if (!window.confirm(msg)) return;
    try {
      await axios.post(`${API}/admin/bookings/${b.id}/reactivate`, { mode });
      toast.success(mode === "paid" ? "Booking is valid again — marked paid." : "Booking re-opened as awaiting payment.");
      loadAll();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not make the booking valid.");
    }
  };

  // Payment verification panel — audit trail + live Square states per booking.
  const [payVerifyFor, setPayVerifyFor] = useState(null);
  const [payVerifyData, setPayVerifyData] = useState(null);
  const [payVerifyLoading, setPayVerifyLoading] = useState(false);

  const togglePayVerify = async (b) => {
    if (payVerifyFor === b.id) {
      setPayVerifyFor(null);
      setPayVerifyData(null);
      return;
    }
    setPayVerifyFor(b.id);
    setPayVerifyData(null);
    setPayVerifyLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/bookings/${b.id}/payment-verification`);
      setPayVerifyData(data);
    } catch {
      toast.error("Couldn't load the payment verification.");
      setPayVerifyFor(null);
    } finally {
      setPayVerifyLoading(false);
    }
  };

  const saveBookingNote = async (bookingId, note) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}/note`, {
        admin_note: note?.transcript ?? "",
        admin_note_audio_url: note?.audio_url ?? "",
      });
      // Optimistically patch local state
      setBookings((prev) =>
        prev.map((x) =>
          x.id === bookingId
            ? { ...x, admin_note: note?.transcript || null, admin_note_audio_url: note?.audio_url || null }
            : x
        )
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save note.");
    }
  };

  const cancelBooking = async (b) => {
    const reason = window.prompt(`Cancel ${b.guest_name || b.guest_email}'s booking?\n\nOptional reason:`, "");
    if (reason === null) return;
    // The policy computes the refund — never typed by hand. Fetch it so the
    // confirmation shows the exact figure and rule before anything happens.
    try {
      const { data } = await axios.get(`${API}/admin/bookings/${b.id}/cancel-preview`);
      setDanger({ kind: "cancel", booking: b, step: 1, reason, policy: data });
    } catch {
      toast.error("Couldn't load the cancellation policy for this booking.");
    }
  };

  const refundBooking = async (b) => {
    const amtStr = window.prompt(`Refund amount (£)?\n\nTotal paid so far: ${gbp(b.payment_type === "full" ? b.total : b.deposit)}\nAlready refunded: ${gbp(b.total_refunded || 0)}\n\nThe money goes straight back onto the guest's card.`, "0");
    if (amtStr === null) return;
    const amount = parseFloat(amtStr) || 0;
    if (amount <= 0) return;
    const reason = window.prompt("Reason for refund? (optional)", "") || "";
    setDanger({ kind: "refund", booking: b, step: 1, amount, reason });
  };

  // One-tap refund from the Cancelled section — uses the policy refund the
  // guest was quoted when they cancelled (minus anything already refunded).
  const refundCancelled = async (b) => {
    const due = Math.max(0, (b.cancel_refund_amount || 0) - (b.total_refunded || 0));
    if (due <= 0) return;
    setDanger({ kind: "refund", booking: b, step: 1, amount: due, reason: `Cancellation refund — ${b.cancel_refund_band || "policy"}` });
  };

  // Actually perform the destructive action after BOTH confirmation steps.
  const executeDanger = async () => {
    const { kind, booking: b } = danger;
    try {
      if (kind === "delete") {
        await axios.delete(`${API}/admin/bookings/${b.id}`);
        toast.success("Booking deleted.");
      } else if (kind === "cancel") {
        const { data } = await axios.post(`${API}/admin/bookings/${b.id}/cancel`, { reason: danger.reason || "" });
        if (data.refund_error) {
          toast.error(`Cancelled — but the £${(data.policy_refund || 0).toFixed(2)} refund failed: ${data.refund_error}. Retry it from the Cancelled section.`);
        } else if (data.refunded > 0) {
          toast.success(`Cancelled — £${data.refunded.toFixed(2)} refunded to the guest's card per policy.`);
        } else if ((data.policy_refund || 0) === 0) {
          toast.success("Cancelled — policy band is credit voucher (no card refund). Issue the voucher from Rewards.");
        } else {
          toast.success("Booking cancelled.");
        }
      } else {
        const { data } = await axios.post(`${API}/admin/bookings/${b.id}/refund`, { amount: danger.amount, reason: danger.reason || "" });
        toast.success(`£${(data.refunded ?? danger.amount).toFixed(2)} refunded to the guest's card.${data.note ? ` ${data.note}` : ""}`);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Action failed — nothing was changed.");
    } finally {
      setDanger(null);
      loadAll();
    }
  };

  const deleteBooking = async (b) => {
    // Fail-safe: the two-step confirmation modal takes it from here.
    setDanger({ kind: "delete", booking: b, step: 1 });
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      toast.error("New passwords don't match.");
      return;
    }
    if (pwForm.next.length < 8) {
      toast.error("Use at least 8 characters.");
      return;
    }
    try {
      await authApi.changePassword(pwForm.current, pwForm.next);
      toast.success("Password updated.");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update password.");
    }
  };

  const logout = () => {
    authApi.logout();
    nav("/admin/login", { replace: true });
  };

  const statusColour = {
    paid: "bg-[#0F0E0E] text-[#F5EFE6]",
    pending: "bg-[#B76E79] text-white",
    expired: "bg-[#6A5F58] text-white",
    failed: "bg-[#8C2E39] text-white",
    external: "bg-[#5B7A8C] text-white",
    cancelled: "bg-[#8C2E39] text-white",
  };

  if (!me) {
    return (
      <div className="min-h-screen bg-[#F5EFE6] flex items-center justify-center text-[#6A5F58] text-sm">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5EFE6]">
      <Seo title="Admin Console" noindex path="/admin" />
      <MorningBriefChip />
      <header className="border-b border-[#DDD1BE] bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#6A5F58] link-underline font-brand"
            data-testid="admin-back-home"
          >
            <ArrowLeft size={14} /> Back
          </Link>
          <div className="font-serif text-xl text-[#0F0E0E]">Walnut Lakes · Admin</div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#6A5F58] link-underline font-brand"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
        <nav className="max-w-7xl mx-auto px-6 md:px-10 flex gap-6 -mb-px overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`py-3 text-sm font-brand tracking-[0.14em] uppercase border-b-2 transition ${
                tab === t.id
                  ? "border-[#B76E79] text-[#0F0E0E]"
                  : "border-transparent text-[#6A5F58] hover:text-[#0F0E0E]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10" data-testid="admin-page">
        {tab === "bookings" && (
          <section>
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <p className="overline text-[#6A5F58]"><span className="marker-line" /> Bookings</p>
                <h2 className="font-serif text-3xl md:text-4xl mt-4 text-[#14100F]">All reservations</h2>
              </div>
              <button
                onClick={() => setShowManualForm(true)}
                className="btn-primary font-brand tracking-[0.14em] text-xs inline-flex items-center gap-2"
                data-testid="admin-add-manual-booking"
              >
                <Plus size={13} /> Add manual booking
              </button>
            </div>

            {/* Source tabs — Website (direct bookings), Pitchup, Booking.com */}
            <div className="mt-6 inline-flex rounded-full border border-[#DDD1BE] overflow-hidden bg-white" data-testid="bookings-source-tabs">
              {[
                { key: "website", label: "Website" },
                { key: "pitchup", label: "Pitchup" },
                { key: "bookingcom", label: "Booking.com" },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setBookingSourceTab(t.key)}
                  className={`px-4 h-10 text-[10px] font-brand tracking-[0.12em] uppercase transition inline-flex items-center gap-1.5 ${bookingSourceTab === t.key ? "bg-[#0F0E0E] text-[#F5EFE6]" : "bg-white text-[#6A5F58] hover:bg-[#F5EFE6]"}`}
                  data-testid={`bookings-tab-${t.key}`}
                >
                  {t.label}
                  <span className={`px-1.5 py-0.5 rounded-full text-[9px] ${bookingSourceTab === t.key ? "bg-[#B76E79] text-white" : "bg-[#DDD1BE]/60 text-[#6A5F58]"}`}>{tabCounts[t.key]}</span>
                </button>
              ))}
            </div>

            {/* Filters — find a customer by text, pick a single day (everyone arriving/departing/staying) or a date range */}
            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="bookings-range-search">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B76E79] pointer-events-none" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, phone, email, reg, ref…"
                  className="w-64 pl-9 bg-white h-10 rounded-lg"
                  data-testid="bookings-search"
                />
              </div>
              {searching && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="px-4 h-10 rounded-full border border-[#DDD1BE] text-[#6A5F58] hover:bg-[#F5EFE6] text-[10px] font-brand tracking-[0.12em] uppercase transition"
                  data-testid="bookings-search-clear"
                >
                  Clear
                </button>
              )}
              <span className="text-[#DDD1BE] mx-1 hidden sm:inline">|</span>
              <CalendarRange size={15} className="text-[#B76E79]" />
              <span className="text-[#6A5F58] text-xs">Day:</span>
              <Input
                type="date"
                value={dayFilter}
                onChange={(e) => { setDayFilter(e.target.value); setRangeFrom(""); setRangeTo(""); }}
                className="w-40 bg-white h-10 rounded-lg"
                data-testid="bookings-day-filter"
              />
              {dayActive && (
                <button
                  type="button"
                  onClick={() => setDayFilter("")}
                  className="px-4 h-10 rounded-full border border-[#DDD1BE] text-[#6A5F58] hover:bg-[#F5EFE6] text-[10px] font-brand tracking-[0.12em] uppercase transition"
                  data-testid="bookings-day-clear"
                >
                  Clear
                </button>
              )}
              <span className="text-[#6A5F58] text-xs mx-1">or range:</span>
              <Input type="date" value={rangeFrom} onChange={(e) => { setRangeFrom(e.target.value); setDayFilter(""); }} className="w-40 bg-white h-10 rounded-lg" data-testid="bookings-range-from" />
              <span className="text-[#6A5F58] text-xs">to</span>
              <Input type="date" value={rangeTo} onChange={(e) => { setRangeTo(e.target.value); setDayFilter(""); }} className="w-40 bg-white h-10 rounded-lg" data-testid="bookings-range-to" />
              {rangeActive && (
                <button
                  type="button"
                  onClick={() => { setRangeFrom(""); setRangeTo(""); }}
                  className="px-4 h-10 rounded-full border border-[#DDD1BE] text-[#6A5F58] hover:bg-[#F5EFE6] text-[10px] font-brand tracking-[0.12em] uppercase transition"
                  data-testid="bookings-range-clear"
                >
                  Clear
                </button>
              )}
            </div>

            {searching && (
              <div className="mt-4 bg-white border border-[#DDD1BE] rounded-2xl p-4" data-testid="bookings-search-summary">
                <p className="text-sm text-[#14100F]">
                  <strong>{visibleBookings.length} match{visibleBookings.length !== 1 ? "es" : ""}</strong> for &ldquo;{search.trim()}&rdquo; across all bookings (every source)
                </p>
              </div>
            )}

            {dayActive && (
              <div className="mt-4 bg-white border border-[#DDD1BE] rounded-2xl p-4" data-testid="bookings-day-summary">
                <p className="text-sm text-[#14100F]">
                  On <strong>{dayFilter}</strong>: <strong>{visibleBookings.length} booking{visibleBookings.length !== 1 ? "s" : ""}</strong>
                  <span className="text-[#6A5F58]"> ({dayCounts.arriving} arriving · {dayCounts.departing} departing · {dayCounts.staying} staying through)</span>
                </p>
              </div>
            )}

            {rangeActive && (
              <div className="mt-4 bg-white border border-[#DDD1BE] rounded-2xl p-4" data-testid="bookings-range-summary">
                <p className="text-sm text-[#14100F]">
                  Between <strong>{rangeFrom}</strong> and <strong>{rangeTo}</strong>:{" "}
                  <strong>{visibleBookings.length} booking{visibleBookings.length !== 1 ? "s" : ""}</strong>
                  <span className="text-[#6A5F58]"> ({rangeCounts.paid} paid · {rangeCounts.pending} pending · {rangeCounts.channel} channel)</span>
                  {" "}· <strong>{rangeBlocks.length} manual block{rangeBlocks.length !== 1 ? "s" : ""}</strong>
                </p>
                {rangeBlocks.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="bookings-range-blocks">
                    {rangeBlocks.map((bl) => (
                      <span
                        key={bl.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C89B5F]/10 border border-[#C89B5F]/30 text-[#8a6a3a] text-[11px]"
                        data-testid={`bookings-range-block-${bl.id}`}
                      >
                        <Ban size={11} />
                        {rangeUnitName(bl.unit_type, bl.unit_id)} · {bl.start_date} → {bl.end_date}{bl.reason ? ` · ${bl.reason}` : ""}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {showManualForm && (
              <div className="mt-6 bg-white border-2 border-[#B76E79] rounded-2xl p-6 md:p-8" data-testid="admin-manual-booking-form">
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-xl text-[#0F0E0E]">New manual booking</h3>
                  <button onClick={() => setShowManualForm(false)} className="text-[#6A5F58] hover:text-[#0F0E0E]" data-testid="manual-close">
                    <X size={18} />
                  </button>
                </div>
                <p className="text-xs text-[#6A5F58] mt-1">
                  For phone / email / walk-in reservations. If &ldquo;paid&rdquo;, confirmation emails fire automatically.
                </p>
                <form onSubmit={submitManual} className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Guest name</Label>
                    <Input value={manualForm.guest_name} onChange={(e) => setManualForm({ ...manualForm, guest_name: e.target.value })} required className="h-10 bg-[#F5EFE6] mt-1" data-testid="manual-name" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Email</Label>
                    <Input type="email" value={manualForm.guest_email} onChange={(e) => setManualForm({ ...manualForm, guest_email: e.target.value })} required className="h-10 bg-[#F5EFE6] mt-1" data-testid="manual-email" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Phone</Label>
                    <Input value={manualForm.guest_phone} onChange={(e) => setManualForm({ ...manualForm, guest_phone: e.target.value })} className="h-10 bg-[#F5EFE6] mt-1" data-testid="manual-phone" />
                  </div>

                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Unit type</Label>
                    <Select value={manualForm.unit_type} onValueChange={(v) => setManualForm({ ...manualForm, unit_type: v, unit_id: "" })}>
                      <SelectTrigger className="h-10 bg-[#F5EFE6] mt-1" data-testid="manual-unit-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {units.map((u) => <SelectItem key={u.type} value={u.type}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Unit</Label>
                    <Select value={manualForm.unit_id} onValueChange={(v) => setManualForm({ ...manualForm, unit_id: v })}>
                      <SelectTrigger className="h-10 bg-[#F5EFE6] mt-1" data-testid="manual-unit-id"><SelectValue placeholder="Pick a unit" /></SelectTrigger>
                      <SelectContent>
                        {(units.find((u) => u.type === manualForm.unit_type)?.units || []).map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Check-in</Label>
                    <Input type="date" value={manualForm.start_date} onChange={(e) => setManualForm({ ...manualForm, start_date: e.target.value })} required className="h-10 bg-[#F5EFE6] mt-1" data-testid="manual-start" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Check-out</Label>
                    <Input type="date" value={manualForm.end_date} onChange={(e) => setManualForm({ ...manualForm, end_date: e.target.value })} required className="h-10 bg-[#F5EFE6] mt-1" data-testid="manual-end" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Guests</Label>
                      <Input type="number" min={1} max={10} value={manualForm.guests} onChange={(e) => setManualForm({ ...manualForm, guests: Number(e.target.value) })} className="h-10 bg-[#F5EFE6] mt-1" data-testid="manual-guests" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Dogs</Label>
                      <Input type="number" min={0} max={2} value={manualForm.dogs} onChange={(e) => setManualForm({ ...manualForm, dogs: Number(e.target.value) })} className="h-10 bg-[#F5EFE6] mt-1" data-testid="manual-dogs" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Payment status</Label>
                    <Select value={manualForm.payment_status} onValueChange={(v) => setManualForm({ ...manualForm, payment_status: v })}>
                      <SelectTrigger className="h-10 bg-[#F5EFE6] mt-1" data-testid="manual-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paid">Paid (already received)</SelectItem>
                        <SelectItem value="pending">Pending (awaiting payment)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Notes (optional)</Label>
                    <Input value={manualForm.notes} onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })} placeholder="e.g. paid by bank transfer" className="h-10 bg-[#F5EFE6] mt-1" data-testid="manual-notes" />
                  </div>

                  <div className="md:col-span-3 flex justify-end gap-2 pt-2">
                    <button type="button" onClick={() => setShowManualForm(false)} className="btn-ghost text-xs font-brand tracking-[0.14em]">Cancel</button>
                    <button type="submit" className="btn-primary text-xs font-brand tracking-[0.14em] inline-flex items-center gap-1.5" data-testid="manual-submit">
                      <Save size={13} /> Create booking
                    </button>
                  </div>
                </form>
              </div>
            )}
            {/* ── Activity board — traffic-light sections (tap to expand, tap again to
                retract) so any customer action is spotted at a glance: GREEN new
                bookings · AMBER guest changes + unfinished payments · RED cancellations. */}
            <div className="mt-8 space-y-4" data-testid="activity-board">
              {newBookings.length > 0 && (
                <div className="bg-white border-2 border-[#3B8C4E]/50 rounded-2xl overflow-hidden" data-testid="new-bookings">
                  <button
                    type="button"
                    onClick={() => setShowNew((v) => !v)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left"
                    data-testid="new-bookings-toggle"
                  >
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-[#3B8C4E] font-semibold">
                        New bookings · last 7 days · {newBookings.length}
                      </p>
                      <p className="mt-1 text-xs text-[#6A5F58]">
                        Every booking that came in this week, newest first — any channel. Tap a row to find it in the list below.
                      </p>
                    </div>
                    {showNew ? <ChevronUp size={16} className="text-[#6A5F58] shrink-0" /> : <ChevronDown size={16} className="text-[#6A5F58] shrink-0" />}
                  </button>
                  {showNew && (
                    <div className="border-t border-[#3B8C4E]/20">
                      {newBookings.map((b) => (
                        <div
                          key={b.id}
                          className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-[#EDE3D2] last:border-0 bg-[#3B8C4E]/[0.04] cursor-pointer hover:bg-[#3B8C4E]/[0.08] transition"
                          data-testid={`new-booking-${b.id}`}
                          onClick={() => setSearch(b.guest_name || b.guest_email || "")}
                          title="Find this booking in the list below"
                        >
                          <div className="min-w-[240px]">
                            <p className="font-medium text-[#14100F]">{b.guest_name || "—"}</p>
                            <p className="text-xs text-[#6A5F58] mt-0.5">
                              {incompleteUnitLabel(b)} · {b.start_date} → {b.end_date} · £{(b.total || 0).toFixed(2)}
                              {b.created_at ? ` · booked ${timeAgo(b.created_at)}` : ""}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold ${statusColour[b.payment_status] || "bg-[#DDD1BE]"}`} data-testid={`new-booking-status-${b.id}`}>
                              {(b.source || "").startsWith("external:") ? `${(b.source || "").split(":")[1]} guest` : b.payment_status}
                            </span>
                            <span className="text-[10px] text-[#6A5F58] uppercase tracking-[0.1em]">View →</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {modifiedBookings.length > 0 && (
                <div className="bg-white border-2 border-[#C89B5F]/60 rounded-2xl overflow-hidden" data-testid="modified-bookings">
                  <button
                    type="button"
                    onClick={() => setShowModified((v) => !v)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left"
                    data-testid="modified-bookings-toggle"
                  >
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-[#C89B5F] font-semibold">
                        Changed by guests · {modifiedBookings.length}
                      </p>
                      <p className="mt-1 text-xs text-[#6A5F58]">
                        Guests who edited their booking online (dates, party size, dogs). A negative amount means a refund is due.
                      </p>
                    </div>
                    {showModified ? <ChevronUp size={16} className="text-[#6A5F58] shrink-0" /> : <ChevronDown size={16} className="text-[#6A5F58] shrink-0" />}
                  </button>
                  {showModified && (
                    <div className="border-t border-[#C89B5F]/25">
                      {modifiedBookings.map((b) => {
                        const delta = Number(b.modification_delta || 0);
                        return (
                          <div
                            key={b.id}
                            className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-[#EDE3D2] last:border-0 bg-[#C89B5F]/[0.05] cursor-pointer hover:bg-[#C89B5F]/[0.1] transition"
                            data-testid={`modified-${b.id}`}
                            onClick={() => setSearch(b.guest_name || b.guest_email || "")}
                            title="Find this booking in the list below"
                          >
                            <div className="min-w-[240px]">
                              <p className="font-medium text-[#14100F]">{b.guest_name || "—"}</p>
                              <p className="text-xs text-[#6A5F58] mt-0.5">
                                {incompleteUnitLabel(b)} · now {b.start_date} → {b.end_date} · {b.guests} guests{b.dogs ? ` · ${b.dogs} dog${b.dogs === 1 ? "" : "s"}` : ""}
                                {b.modified_at ? ` · changed ${timeAgo(b.modified_at)}` : ""}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold bg-[#C89B5F]/15 text-[#8a6a3a]">
                                Changed
                              </span>
                              {delta > 0 && (
                                <span className="px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold bg-[#3B8C4E]/15 text-[#3B8C4E]" data-testid={`modified-delta-${b.id}`}>
                                  Owes £{delta.toFixed(2)} more
                                </span>
                              )}
                              {delta < 0 && (
                                <span className="px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold bg-[#8C2E39]/15 text-[#8C2E39]" data-testid={`modified-delta-${b.id}`}>
                                  Refund £{Math.abs(delta).toFixed(2)} due
                                </span>
                              )}
                              <span className="text-[10px] text-[#6A5F58] uppercase tracking-[0.1em]">View →</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Guests who reached the payment page but never completed — details
                  captured at checkout, so Justina can call and walk them through it. */}
              {incompleteBookings.length > 0 && (
                <div className="bg-white border border-[#C89B5F]/60 rounded-2xl overflow-hidden" data-testid="incomplete-payments">
                  <button
                    type="button"
                    onClick={() => setShowIncomplete((v) => !v)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left"
                    data-testid="incomplete-payments-toggle"
                  >
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-[#C89B5F] font-semibold">
                        Didn&rsquo;t finish payment · {incompleteBookings.length}
                      </p>
                      <p className="mt-1 text-xs text-[#6A5F58]">
                        These guests reached the payment page but never completed. They also appear in the
                        list above — this is just a quick call-list with their details. Unfinished
                        checkouts expire automatically after an hour.
                      </p>
                    </div>
                    {showIncomplete ? <ChevronUp size={16} className="text-[#6A5F58] shrink-0" /> : <ChevronDown size={16} className="text-[#6A5F58] shrink-0" />}
                  </button>
                  {showIncomplete && (
                    <div className="border-t border-[#EDE3D2]">
                      {incompleteBookings.map((b) => (
                        <div
                          key={b.id}
                          className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-[#EDE3D2] last:border-0"
                          data-testid={`incomplete-${b.id}`}
                        >
                          <div className="min-w-[220px]">
                            <p className="font-medium text-[#14100F]">{b.guest_name || "—"}</p>
                            <p className="text-xs text-[#6A5F58] mt-0.5">
                              {b.start_date} → {b.end_date} · {incompleteUnitLabel(b)} · £{(b.total || 0).toFixed(2)}
                              {b.created_at ? ` · tried ${timeAgo(b.created_at)}` : ""}
                            </p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs">
                              {b.guest_phone && (
                                <a href={`tel:${(b.guest_phone || "").replace(/\s/g, "")}`} className="inline-flex items-center gap-1 text-[#0F0E0E] hover:text-[#B76E79]">
                                  <Phone size={11} /> {b.guest_phone}
                                </a>
                              )}
                              {b.guest_email && (
                                <a href={`mailto:${b.guest_email}`} className="inline-flex items-center gap-1 text-[#0F0E0E] hover:text-[#B76E79]">
                                  <Mail size={11} /> {b.guest_email}
                                </a>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {b.payment_status === "pending" ? (
                              <span className="px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold bg-[#C89B5F]/15 text-[#8a6a3a]" data-testid={`incomplete-status-${b.id}`}>
                                Awaiting payment
                              </span>
                            ) : (
                              <span className="px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold bg-[#DDD1BE]/60 text-[#6A5F58]" data-testid={`incomplete-status-${b.id}`}>
                                Expired
                              </span>
                            )}
                            {(b.payment_status === "pending" || b.payment_status === "expired") && (
                              <button
                                type="button"
                                onClick={() => resendPaymentLink(b)}
                                className="px-3 py-1.5 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition text-[10px] font-brand tracking-[0.12em] uppercase"
                                data-testid={`incomplete-resend-${b.id}`}
                              >
                                {b.payment_status === "expired" ? "Revive + new link" : "Resend link"}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => deleteBooking(b)}
                              className="p-1.5 text-[#6A5F58] hover:text-[#8C2E39] transition"
                              title="Remove"
                              data-testid={`incomplete-delete-${b.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {cancelledBookings.length > 0 && (
                <div className="bg-white border-2 border-[#8C2E39]/50 rounded-2xl overflow-hidden" data-testid="cancelled-bookings">
                  <button
                    type="button"
                    onClick={() => setShowCancelled((v) => !v)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left"
                    data-testid="cancelled-bookings-toggle"
                  >
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-[#8C2E39] font-semibold">
                        Cancelled bookings · {cancelledBookings.length}
                        {refundsDueTotal > 0 && <span className="ml-2 px-2 py-0.5 rounded-full bg-[#8C2E39] text-white text-[9px]">£{refundsDueTotal.toFixed(2)} refunds due</span>}
                      </p>
                      <p className="mt-1 text-xs text-[#6A5F58]">
                        Every cancellation lands here — guest, reason, policy band and refund state. A red
                        &ldquo;refund due&rdquo; means the money has NOT gone back yet: one tap sends it to the guest&rsquo;s card.
                      </p>
                    </div>
                    {showCancelled ? <ChevronUp size={16} className="text-[#6A5F58] shrink-0" /> : <ChevronDown size={16} className="text-[#6A5F58] shrink-0" />}
                  </button>
                  {showCancelled && (
                    <div className="border-t border-[#8C2E39]/20">
                      {cancelledBookings.map((b) => {
                        const refundDue = Math.max(0, (b.cancel_refund_amount || 0) - (b.total_refunded || 0));
                        return (
                          <div
                            key={b.id}
                            className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-[#EDE3D2] last:border-0 bg-[#8C2E39]/[0.04]"
                            data-testid={`cancelled-${b.id}`}
                          >
                            <div className="min-w-[240px]">
                              <p className="font-medium text-[#14100F]">{b.guest_name || "—"}</p>
                              <p className="text-xs text-[#6A5F58] mt-0.5">
                                {incompleteUnitLabel(b)} · {b.start_date} → {b.end_date} · £{(b.total || 0).toFixed(2)}
                                {b.cancelled_at ? ` · cancelled ${timeAgo(b.cancelled_at)}` : ""}
                                {b.cancelled_by ? ` by ${b.cancelled_by === "guest" ? "the guest" : "the office"}` : ""}
                              </p>
                              {b.cancel_reason && (
                                <p className="text-[11px] text-[#8C2E39] mt-1 italic" data-testid={`cancelled-reason-${b.id}`}>“{b.cancel_reason}”</p>
                              )}
                              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs">
                                {b.guest_phone && (
                                  <a href={`tel:${(b.guest_phone || "").replace(/\s/g, "")}`} className="inline-flex items-center gap-1 text-[#0F0E0E] hover:text-[#B76E79]">
                                    <Phone size={11} /> {b.guest_phone}
                                  </a>
                                )}
                                {b.guest_email && (
                                  <a href={`mailto:${b.guest_email}`} className="inline-flex items-center gap-1 text-[#0F0E0E] hover:text-[#B76E79]">
                                    <Mail size={11} /> {b.guest_email}
                                  </a>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold bg-[#8C2E39] text-white">
                                Cancelled
                              </span>
                              <a
                                href={`/admin/invoice/${b.id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="px-3 py-1.5 rounded-full border border-[#DDD1BE] text-[#3A322E] hover:bg-[#F5EFE6] transition text-[10px] font-brand tracking-[0.12em] uppercase inline-flex items-center gap-1"
                                data-testid={`cancelled-invoice-${b.id}`}
                                title="Invoice & cancellation record — payments and refunds"
                              >
                                <FileText size={11} /> Invoice
                              </a>
                              {refundDue > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => refundCancelled(b)}
                                  className="px-3 py-1.5 rounded-full bg-[#8C2E39] text-white hover:bg-[#6d2230] transition text-[10px] font-brand tracking-[0.12em] uppercase"
                                  data-testid={`cancelled-refund-${b.id}`}
                                  title={`Policy: ${b.cancel_refund_band || "—"} — refund goes straight to the guest's card`}
                                >
                                  Refund £{refundDue.toFixed(2)}
                                </button>
                              ) : (b.total_refunded || 0) > 0 ? (
                                <span className="px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold bg-[#3B8C4E]/15 text-[#3B8C4E]" data-testid={`cancelled-refunded-${b.id}`}>
                                  Refunded £{(b.total_refunded || 0).toFixed(2)} ✓
                                </span>
                              ) : (
                                <span className="px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold bg-[#DDD1BE]/60 text-[#6A5F58]">
                                  No refund due
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="mt-8 bg-white border border-[#DDD1BE] rounded-2xl overflow-hidden" data-testid="all-bookings-section">
              <button
                type="button"
                onClick={() => setShowAllBookings((v) => !v)}
                className="w-full flex items-center justify-between gap-4 p-5 text-left border-b border-[#DDD1BE]"
                data-testid="all-bookings-toggle"
                title="Tap to hide or show the full reservations list"
              >
                <p className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-semibold">
                  All reservations · {visibleBookings.length}
                </p>
                {allBookingsOpen ? <ChevronUp size={16} className="text-[#6A5F58] shrink-0" /> : <ChevronDown size={16} className="text-[#6A5F58] shrink-0" />}
              </button>
              {allBookingsOpen && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-bookings-table">
                  <thead className="bg-[#F5EFE6] text-[#6A5F58] uppercase tracking-[0.18em] text-xs">
                    <tr>
                      <th className="text-left p-4">Guest</th>
                      <th className="text-left p-4">Phone</th>
                      <th className="text-left p-4">Email</th>
                      <th className="text-left p-4">Unit</th>
                      <th className="text-left p-4">Dates</th>
                      <th className="text-left p-4">Guests · Dogs</th>
                      <th className="text-left p-4">Total</th>
                      <th className="text-left p-4">Deposit / Paid</th>
                      <th className="text-left p-4">Balance</th>
                      <th className="text-left p-4">Status</th>
                      <th className="text-left p-4">Booked</th>
                      <th className="text-right p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleBookings.length === 0 && (
                      <tr><td colSpan={12} className="p-8 text-center text-[#6A5F58]">{rangeActive ? "No bookings in this period." : dayActive ? "No bookings on this day." : `No ${bookingSourceTab === "website" ? "website" : bookingSourceTab === "pitchup" ? "Pitchup" : "Booking.com"} bookings yet.`}</td></tr>
                    )}
                    {visibleBookings.map((b) => {
                      const ut = units.find((u) => u.type === b.unit_type);
                      const unit = ut?.units.find((u) => u.id === b.unit_id);
                      const unitName = unit?.name || b.unit_id;
                      const isPaid = b.payment_status === "paid";
                      // Prefer the ACTUAL recorded online collection over the
                      // deposit/total assumption — crediting a check-in-page
                      // payment to the wrong bucket once double-counted money here.
                      const initialPaid = isPaid ? (Number(b.online_collected || 0) || (b.payment_type === "full" ? b.total : b.deposit)) : 0;
                      const paidNow = initialPaid + (b.balance_paid_amount || 0);
                      const balance = Math.max(0, (b.total || 0) - paidNow);
                      const isPending = b.payment_status === "pending";
                      const isExpiredOrFailed = b.payment_status === "expired" || b.payment_status === "failed";
                      const canSendLink = b.payment_status !== "cancelled" && (isPending || isExpiredOrFailed || balance > 0);
                      const isExternal = (b.source || "").startsWith("external:");
                      const externalChannel = isExternal ? (b.source.split(":")[1] || "channel") : null;
                      const bookedAt = b.created_at ? new Date(b.created_at) : null;
                      const bookedAtValid = bookedAt && !Number.isNaN(bookedAt.getTime());
                      const sourceLabel = isExternal ? (externalChannel === "booking" ? "Booking.com" : externalChannel) : b.source === "daisy-concierge" ? "via Daisy" : b.source === "admin-manual" ? "manual" : b.source === "voice-assistant" ? "voice" : "website";
                      return (
                      <React.Fragment key={b.id}>
                      <tr
                        className={`border-t border-[#DDD1BE] ${b.payment_status === "cancelled" ? "bg-[#8C2E39]/[0.06] border-l-4 border-l-[#8C2E39]" : ""}`}
                        data-testid={`admin-booking-row-${b.id}`}
                      >
                        <td className="p-4">
                          <div className="text-[#14100F] font-semibold" data-testid={`admin-booking-name-${b.id}`}>{b.guest_name || "—"}</div>
                          <div className="text-[10px] text-[#6A5F58] font-mono mt-0.5">#{b.booking_id || (b.id || "").slice(0, 8)}</div>
                        </td>
                        <td className="p-4">
                          {b.guest_phone ? (
                            <a
                              href={`tel:${b.guest_phone}`}
                              className="text-[#0F0E0E] font-medium hover:text-[#B76E79] hover:underline"
                              data-testid={`admin-booking-phone-${b.id}`}
                            >
                              {b.guest_phone}
                            </a>
                          ) : (
                            <span className="text-[#8C2E39] text-xs">no phone</span>
                          )}
                        </td>
                        <td className="p-4">
                          {b.guest_email && (
                            <a
                              href={`mailto:${b.guest_email}`}
                              className="text-[#B76E79] text-xs hover:underline break-all"
                              data-testid={`admin-booking-email-${b.id}`}
                            >
                              {b.guest_email}
                            </a>
                          )}
                        </td>
                        <td className="p-4 text-[#14100F]">
                          <div className="font-medium">{unitName || (b.unit_type === "touring" ? "Allocated on arrival" : "—")}</div>
                          <div className="text-xs text-[#6A5F58]">{ut?.name || b.unit_type}</div>
                          {b.sub_type && (
                            <div className="text-[10px] mt-1 uppercase tracking-[0.14em] text-[#B76E79] font-semibold" data-testid={`admin-booking-subtype-${b.id}`}>
                              {b.sub_type} category
                            </div>
                          )}
                          {b.pitch_locked && (
                            <div className="text-[10px] mt-1 uppercase tracking-[0.14em] text-[#3B8C4E] font-semibold" data-testid={`admin-booking-pitchlocked-${b.id}`}>
                              Guest-picked pitch · guaranteed
                            </div>
                          )}
                          {b.unit_type === "touring" && !b.unit_id && b.payment_status !== "cancelled" && (
                            <PitchAssign booking={b} onDone={loadAll} />
                          )}
                          {b.unit_type === "touring" && b.unit_id && b.pitch_assigned_at && (
                            <div className="text-[9px] mt-1 uppercase tracking-[0.12em] text-[#3B8C4E] font-semibold" data-testid={`admin-booking-pitch-assigned-${b.id}`}>
                              ✓ Pitch reserved
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-[#14100F]">
                          <div>{b.start_date} → {b.end_date}</div>
                          <div className="text-xs text-[#6A5F58]">{b.nights} night{b.nights > 1 ? "s" : ""}</div>
                          <div className="text-[10px] text-[#6A5F58] mt-1" data-testid={`admin-booking-times-${b.id}`}>
                            in {b.checkin_time || (b.unit_type === "lodge" ? "14:00" : "13:00")} · out {b.checkout_time || "11:00"}
                          </div>
                          {b.vehicle_reg && (
                            <div className="text-[10px] mt-1 font-mono tracking-[0.08em] text-[#0F0E0E] bg-[#F5EFE6] inline-block px-1.5 py-0.5 rounded" data-testid={`admin-booking-vreg-${b.id}`}>
                              🚗 {b.vehicle_reg}
                            </div>
                          )}
                          {b.special_requests && (
                            <div
                              className="text-[10px] mt-1 text-[#0F0E0E] bg-[#FBF7F1] border-l-2 border-[#B76E79] px-2 py-1 max-w-[220px] leading-snug"
                              data-testid={`admin-booking-requests-${b.id}`}
                              title={b.special_requests}
                            >
                              ✎ {b.special_requests}
                            </div>
                          )}
                          {(b.admin_note || b.admin_note_audio_url) && (
                            <div
                              className="text-[10px] mt-1 text-[#0F0E0E] bg-[#0F0E0E] text-[#F5EFE6] px-2 py-1 max-w-[220px] leading-snug rounded"
                              data-testid={`admin-booking-note-${b.id}`}
                              title={b.admin_note}
                            >
                              📝 {b.admin_note ? (b.admin_note.length > 40 ? b.admin_note.slice(0, 40) + "…" : b.admin_note) : "Voice note attached"}
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-[#14100F] text-xs">
                          {b.guests || 1} guest{(b.guests || 1) > 1 ? "s" : ""}
                          {b.dogs > 0 ? ` · ${b.dogs} dog${b.dogs > 1 ? "s" : ""}` : ""}
                        </td>
                        <td className="p-4 text-[#14100F] font-medium">
                          {isExternal ? <span className="text-xs text-[#5B7A8C] font-semibold uppercase tracking-[0.08em]">via {externalChannel}</span> : gbp(b.total)}
                        </td>
                        <td className="p-4">
                          {isPaid ? (
                            <>
                              <div className="text-[#3B8C4E] font-medium">{gbp(paidNow)}</div>
                              <div className="text-[10px] uppercase tracking-[0.12em] text-[#6A5F58]">
                                {b.payment_type === "full" ? "paid in full" : "deposit paid"}
                              </div>
                            </>
                          ) : isPending ? (
                            <>
                              <div className="text-[#C89B5F] font-medium">{gbp(b.deposit)}</div>
                              <div className="text-[10px] uppercase tracking-[0.12em] text-[#C89B5F]">
                                deposit pending
                              </div>
                            </>
                          ) : (
                            <span className="text-[#6A5F58] text-xs">—</span>
                          )}
                        </td>
                        <td className="p-4">
                          {isExternal ? (
                            <span className="text-[#6A5F58] text-xs">—</span>
                          ) : balance > 0 ? (
                            <>
                              <div className={`font-medium ${isPending ? "text-[#8C2E39]" : "text-[#0F0E0E]"}`}>
                                {gbp(balance)}
                              </div>
                              <div className="text-[10px] uppercase tracking-[0.12em] text-[#6A5F58]">
                                {isPending ? "not paid yet" : "due at arrival"}
                              </div>
                            </>
                          ) : (
                            <span className="text-[#3B8C4E] text-xs font-medium">Settled</span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColour[b.payment_status] || "bg-[#DDD1BE]"}`}>
                            {isExternal ? externalChannel : b.payment_status}
                          </span>
                          {/* £100 lodge deposit state — visible at a glance without opening arrivals */}
                          {b.unit_type === "lodge" && b.deposit_status === "held" && (
                            <div className="mt-1.5">
                              <span className="inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold bg-[#C89B5F] text-white" data-testid={`admin-booking-deposit-${b.id}`}>
                                £100 deposit held{b.deposit_via === "guest-link" ? " · via link" : ""}
                              </span>
                            </div>
                          )}
                          {b.unit_type === "lodge" && b.deposit_status === "released" && (
                            <div className="mt-1.5">
                              <span className="inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold bg-[#3B8C4E] text-white" data-testid={`admin-booking-deposit-${b.id}`}>
                                Deposit released
                              </span>
                            </div>
                          )}
                          {b.unit_type === "lodge" && b.deposit_status === "charged" && (
                            <div className="mt-1.5">
                              <span className="inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold bg-[#8C4E58] text-white" data-testid={`admin-booking-deposit-${b.id}`}>
                                Deposit charged
                              </span>
                            </div>
                          )}
                          {b.unit_type === "lodge" && !b.deposit_status && b.deposit_link_sent_at && !["cancelled", "expired", "failed"].includes(b.payment_status) && (
                            <div className="mt-1.5">
                              <span className="inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold bg-[#DDD1BE] text-[#6A5F58]" data-testid={`admin-booking-deposit-${b.id}`}>
                                Deposit link sent
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="p-4">
                          {bookedAtValid ? (
                            <>
                              <div className="text-xs text-[#14100F] font-medium whitespace-nowrap" data-testid={`admin-booking-created-${b.id}`}>
                                {bookedAt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                              </div>
                              <div className="text-[10px] text-[#6A5F58] whitespace-nowrap">
                                {bookedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                              </div>
                              <span
                                className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.12em] font-semibold ${b.source === "daisy-concierge" ? "bg-[#B76E79]/15 text-[#B76E79]" : "bg-[#DDD1BE]/60 text-[#6A5F58]"}`}
                                data-testid={`admin-booking-source-${b.id}`}
                              >
                                {sourceLabel}
                              </span>
                            </>
                          ) : (
                            <span className="text-[#6A5F58] text-xs">—</span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-1">
                            {isExternal && (
                              <span className="text-[10px] text-[#6A5F58] italic pr-1" data-testid={`admin-booking-external-note-${b.id}`}>
                                managed via {externalChannel}
                              </span>
                            )}
                            {isExternal && (
                              <button
                                onClick={() => (guestEdit === b.id ? setGuestEdit(null) : openGuestEdit(b))}
                                className="p-2 rounded-full text-[#0F0E0E] hover:text-[#B76E79] hover:bg-[#F5EFE6] transition"
                                data-testid={`admin-booking-guestedit-${b.id}`}
                                title="Attach guest details (name / phone / email) from the channel extranet"
                              >
                                {guestEdit === b.id ? <X size={15} /> : <UserPen size={15} />}
                              </button>
                            )}
                            {!isExternal && b.payment_status !== "cancelled" && b.payment_status !== "expired" && (
                              <button
                                onClick={() => (editing === b.id ? setEditing(null) : startEdit(b))}
                                className="p-2 rounded-full text-[#6A5F58] hover:text-[#0F0E0E] hover:bg-[#F5EFE6] transition"
                                data-testid={`admin-booking-edit-${b.id}`}
                                title="Modify dates / guests / dogs"
                              >
                                {editing === b.id ? <X size={15} /> : <Edit3 size={15} />}
                              </button>
                            )}
                            {/* One-tap lodge transfer — also for channel bookings (their unit is our internal allocation) */}
                            <MoveBookingDialog booking={b} onMoved={loadAll} />
                            {!isExternal && (
                              <button
                                onClick={() => togglePayVerify(b)}
                                className={`p-2 rounded-full transition ${payVerifyFor === b.id ? "bg-[#0F0E0E] text-[#F5EFE6]" : "text-[#6A5F58] hover:text-[#0F0E0E] hover:bg-[#F5EFE6]"}`}
                                data-testid={`admin-booking-payverify-${b.id}`}
                                title="Payment verification — every recorded payment + live Square status"
                              >
                                <ReceiptText size={15} />
                              </button>
                            )}
                            <a
                              href={`/admin/invoice/${b.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="p-2 rounded-full text-[#6A5F58] hover:text-[#0F0E0E] hover:bg-[#F5EFE6] transition"
                              data-testid={`admin-booking-invoice-${b.id}`}
                              title="Invoice / receipt — charges, payments and refunds (printable)"
                            >
                              <FileText size={15} />
                            </a>
                            {!isExternal && b.payment_status === "pending" && (
                              <button
                                onClick={() => markPaid(b)}
                                className="p-2 rounded-full text-[#3B8C4E] hover:text-[#2A6636] hover:bg-[#3B8C4E]/10 transition"
                                data-testid={`admin-booking-markpaid-${b.id}`}
                                title="Mark as paid (fires confirmation emails)"
                              >
                                <CheckCircle2 size={15} />
                              </button>
                            )}
                            {!isExternal && isExpiredOrFailed && (
                              <>
                                <button
                                  onClick={() => reactivateBooking(b, "paid")}
                                  className="p-2 rounded-full text-[#3B8C4E] hover:text-[#2A6636] hover:bg-[#3B8C4E]/10 transition"
                                  data-testid={`admin-booking-makevalid-paid-${b.id}`}
                                  title="Make valid — the guest HAS paid (e.g. showing in Square)"
                                >
                                  <CheckCircle2 size={15} />
                                </button>
                                <button
                                  onClick={() => reactivateBooking(b, "pending")}
                                  className="p-2 rounded-full text-[#C89B5F] hover:text-[#A87B3D] hover:bg-[#C89B5F]/10 transition"
                                  data-testid={`admin-booking-makevalid-pending-${b.id}`}
                                  title="Make valid — re-open as awaiting payment (fresh 60-min hold)"
                                >
                                  <RotateCcw size={15} />
                                </button>
                              </>
                            )}
                            {!isExternal && canSendLink && (
                              <button
                                onClick={() => resendPaymentLink(b)}
                                className="p-2 rounded-full text-[#B76E79] hover:text-[#8C4E58] hover:bg-[#B76E79]/10 transition"
                                data-testid={`admin-booking-sendlink-${b.id}`}
                                title={isExpiredOrFailed
                                  ? `Send a fresh payment link for ${gbp(b.payment_type === "deposit" ? b.deposit : b.total)} — revives the booking to pending`
                                  : `Email the guest a payment link for the outstanding ${gbp(balance)}`}
                              >
                                <Link2 size={15} />
                              </button>
                            )}
                            {!isExternal && b.payment_status === "paid" && (
                              <button
                                onClick={() => refundBooking(b)}
                                className="p-2 rounded-full text-[#B76E79] hover:text-[#8C4E58] hover:bg-[#B76E79]/10 transition"
                                data-testid={`admin-booking-refund-${b.id}`}
                                title="Log a refund"
                              >
                                <RotateCcw size={15} />
                              </button>
                            )}
                            {!isExternal && b.payment_status !== "cancelled" && (
                              <button
                                onClick={() => cancelBooking(b)}
                                className="p-2 rounded-full text-[#8C2E39] hover:text-[#5D1F27] hover:bg-[#8C2E39]/10 transition"
                                data-testid={`admin-booking-cancel-${b.id}`}
                                title="Cancel booking"
                              >
                                <Ban size={15} />
                              </button>
                            )}
                            {!isExternal && (
                              <button
                                onClick={() => deleteBooking(b)}
                                className="p-2 rounded-full text-[#6A5F58] hover:text-[#8C2E39] hover:bg-[#8C2E39]/10 transition"
                                data-testid={`admin-booking-delete-${b.id}`}
                                title="Permanently delete"
                              >
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {payVerifyFor === b.id && (
                        <tr className="bg-[#FBF7F1]" data-testid={`payment-verify-panel-${b.id}`}>
                          <td colSpan={12} className="p-4">
                            {payVerifyLoading && (
                              <div className="text-[#6A5F58] text-sm py-2"><Loader2 className="animate-spin inline mr-2" size={14} /> Checking Square…</div>
                            )}
                            {payVerifyData && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold mb-2">Payment record</p>
                                  {payVerifyData.events.length === 0 && <p className="text-xs text-[#6A5F58]">No payments recorded yet.</p>}
                                  <div className="space-y-1.5">
                                    {payVerifyData.events.map((ev, i) => (
                                      <div key={i} className="flex items-start justify-between gap-3 text-xs border-b border-[#EDE3D2] pb-1.5" data-testid={`payment-verify-event-${b.id}-${i}`}>
                                        <div className="min-w-0">
                                          <p className="text-[#14100F] font-medium">{ev.label}</p>
                                          <p className="text-[10px] text-[#6A5F58]">
                                            {ev.at ? new Date(ev.at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : ""}
                                            {ev.detail ? ` · ${ev.detail}` : ""}{ev.ref ? ` · ref …${ev.ref}` : ""}
                                          </p>
                                        </div>
                                        {ev.amount != null && (
                                          <span className={`font-mono font-semibold whitespace-nowrap ${ev.amount < 0 ? "text-[#8C2E39]" : "text-[#3B8C4E]"}`}>
                                            {ev.amount < 0 ? "−" : "+"}£{Math.abs(ev.amount).toFixed(2)}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                  <p className="mt-2 text-xs" data-testid={`payment-verify-outstanding-${b.id}`}>
                                    {payVerifyData.outstanding > 0
                                      ? <span className="font-semibold text-[#8C2E39]">Outstanding: £{payVerifyData.outstanding.toFixed(2)}</span>
                                      : <span className="font-semibold text-[#3B8C4E]">Fully settled — nothing outstanding</span>}
                                    <span className="text-[#6A5F58]"> · total £{payVerifyData.total.toFixed(2)} ({payVerifyData.payment_type})</span>
                                  </p>
                                </div>
                                <div>
                                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold mb-2">Live in Square</p>
                                  {payVerifyData.square.length === 0 && <p className="text-xs text-[#6A5F58]">No Square orders linked to this booking.</p>}
                                  <div className="space-y-2">
                                    {payVerifyData.square.map((s, i) => (
                                      <div key={i} className="rounded-lg border border-[#DDD1BE] bg-white p-2.5" data-testid={`payment-verify-square-${b.id}-${i}`}>
                                        <div className="flex items-center justify-between gap-2">
                                          <p className="text-xs font-medium text-[#14100F]">{s.label} <span className="text-[#6A5F58] font-mono text-[10px]">…{s.order_ref}</span></p>
                                          {s.found ? (
                                            <span className={`px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.1em] font-semibold ${s.state === "COMPLETED" ? "bg-[#3B8C4E] text-white" : s.state === "CANCELED" ? "bg-[#8C2E39] text-white" : "bg-[#C89B5F] text-white"}`} data-testid={`payment-verify-square-state-${b.id}-${i}`}>
                                              {s.state}
                                            </span>
                                          ) : (
                                            <span className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.1em] font-semibold bg-[#DDD1BE] text-[#6A5F58]">not found</span>
                                          )}
                                        </div>
                                        {s.found && (
                                          <p className="text-[10px] text-[#6A5F58] mt-1">
                                            Order £{Number(s.total || 0).toFixed(2)}
                                            {(s.tenders || []).map((t, j) => (
                                              <span key={j} className="ml-2 text-[#14100F]">
                                                {t.brand ? `${String(t.brand).toLowerCase()} ` : ""}••{t.card || "????"} £{Number(t.amount || 0).toFixed(2)} · {t.status}
                                                {t.refunded > 0 && <span className="text-[#8C2E39] font-semibold"> · refunded £{Number(t.refunded).toFixed(2)}</span>}
                                              </span>
                                            ))}
                                            {(s.tenders || []).length === 0 && " · no payment taken"}
                                          </p>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                      {editing === b.id && (
                        <tr className="bg-[#F5EFE6]" data-testid={`admin-booking-edit-panel-${b.id}`}>
                          <td colSpan={12} className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                              <div>
                                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Check-in</Label>
                                <Input type="date" value={editForm.start_date} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} className="h-10 bg-white mt-1" />
                              </div>
                              <div>
                                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Check-out</Label>
                                <Input type="date" value={editForm.end_date} onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })} className="h-10 bg-white mt-1" />
                              </div>
                              <div>
                                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Guests</Label>
                                <Input type="number" min={1} max={10} value={editForm.guests} onChange={(e) => setEditForm({ ...editForm, guests: Number(e.target.value) })} className="h-10 bg-white mt-1" />
                              </div>
                              <div>
                                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Dogs</Label>
                                <Input type="number" min={0} max={2} value={editForm.dogs} onChange={(e) => setEditForm({ ...editForm, dogs: Number(e.target.value) })} className="h-10 bg-white mt-1" />
                              </div>
                              <div>
                                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Unit</Label>
                                <Select value={editForm.unit_id} onValueChange={(v) => setEditForm({ ...editForm, unit_id: v })}>
                                  <SelectTrigger className="h-10 bg-white mt-1" data-testid={`admin-booking-edit-unit-${b.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {(units.find((u) => u.type === b.unit_type)?.units || []).map((u) => (
                                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                {b.unit_type === "lodge" && editForm.unit_id !== b.unit_id && (
                                  <p className="text-[9px] text-[#B76E79] mt-1 leading-snug" data-testid={`admin-booking-edit-unit-hint-${b.id}`}>
                                    Transfer: frees {unitName} and blocks the new lodge for these dates.
                                  </p>
                                )}
                              </div>
                              <div>
                                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Arrival time</Label>
                                <select
                                  value={editForm.checkin_time || b.checkin_time || (b.unit_type === "lodge" ? "14:00" : "13:00")}
                                  onChange={(e) => setEditForm({ ...editForm, checkin_time: e.target.value })}
                                  className="h-10 bg-white mt-1 w-full rounded-md border border-[#DDD1BE] px-3 text-sm"
                                  data-testid={`admin-booking-edit-checkin-${b.id}`}
                                >
                                  {(b.unit_type === "lodge" ? TIME_SLOTS_CHECKIN_LODGE : TIME_SLOTS_CHECKIN_TOURING).map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="flex items-end gap-2">
                                <button
                                  onClick={() => saveEdit(b)}
                                  className="btn-primary text-xs font-brand tracking-[0.14em] inline-flex items-center gap-1.5 h-10"
                                  data-testid={`admin-booking-save-${b.id}`}
                                >
                                  <Save size={13} /> Save
                                </button>
                              </div>
                            </div>

                            {/* Admin voice/text note — private, admin only */}
                            <div className="mt-5 pt-5 border-t border-[#DDD1BE]">
                              <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">
                                Internal admin note · voice + text (never shown to guests)
                              </Label>
                              <div className="mt-2">
                                <VoiceNoteRecorder
                                  value={{ audio_url: b.admin_note_audio_url || "", transcript: b.admin_note || "" }}
                                  onChange={(nv) => saveBookingNote(b.id, nv)}
                                  placeholder="Record a voice note for this booking (auto-transcribed) or type here directly."
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {guestEdit === b.id && isExternal && (
                        <tr className="bg-[#F5EFE6]/60">
                          <td colSpan={12} className="p-6">
                            <div className="bg-white border border-[#DDD1BE] rounded-xl p-5 max-w-2xl" data-testid={`guestedit-panel-${b.id}`}>
                              <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">
                                Guest details · {externalChannel}
                              </p>
                              <p className="text-[11px] text-[#6A5F58] mt-1">
                                {externalChannel} doesn't share these via the calendar sync — copy them from the extranet.
                                Saved details survive every re-sync.
                              </p>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                                <div>
                                  <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Name</Label>
                                  <Input value={guestForm.name} onChange={(e) => setGuestForm({ ...guestForm, name: e.target.value })} className="h-10 bg-white mt-1" placeholder="Guest name" data-testid={`guestedit-name-${b.id}`} />
                                </div>
                                <div>
                                  <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Phone</Label>
                                  <Input value={guestForm.phone} onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })} className="h-10 bg-white mt-1" placeholder="07…" data-testid={`guestedit-phone-${b.id}`} />
                                </div>
                                <div>
                                  <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">Email</Label>
                                  <Input value={guestForm.email} onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })} className="h-10 bg-white mt-1" placeholder="guest@…" data-testid={`guestedit-email-${b.id}`} />
                                </div>
                              </div>
                              <div className="flex gap-2 mt-4">
                                <button onClick={() => saveGuestDetails(b)} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition text-[10px] font-brand tracking-[0.12em] uppercase" data-testid={`guestedit-save-${b.id}`}>
                                  <Save size={13} /> Save details
                                </button>
                                <button onClick={() => setGuestEdit(null)} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full border border-[#DDD1BE] text-[#6A5F58] hover:bg-[#F5EFE6] transition text-[10px] font-brand tracking-[0.12em] uppercase" data-testid={`guestedit-cancel-${b.id}`}>
                                  <X size={13} /> Cancel
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          </section>
        )}

        {tab === "enquiries" && <AdminEnquiries />}

        {tab === "blocks" && (
          <section>
            <BookingCalendar units={units} />

            <hr className="my-12 border-[#DDD1BE]" />

            <p className="overline text-[#6A5F58]"><span className="marker-line" /> Availability blocks</p>
            <h2 className="font-serif text-3xl md:text-4xl mt-4 text-[#14100F]">Close off dates</h2>
            <p className="mt-3 text-[#6A5F58] text-sm max-w-lg">
              Prefer form entry? Add a block by exact dates below — same result as clicking the calendar above.
            </p>

            <form onSubmit={createBlock} data-testid="admin-block-form" className="mt-8 bg-white border border-[#DDD1BE] rounded-2xl p-6 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Unit type</Label>
                  <Select value={form.unit_type} onValueChange={(v) => setForm({ ...form, unit_type: v, unit_id: "" })}>
                    <SelectTrigger className="mt-2 bg-[#F5EFE6] h-11 rounded-lg" data-testid="block-unit-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{units.map((u) => (<SelectItem key={u.type} value={u.type}>{u.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Unit</Label>
                  <Select value={form.unit_id} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
                    <SelectTrigger className="mt-2 bg-[#F5EFE6] h-11 rounded-lg" data-testid="block-unit-id"><SelectValue placeholder="Pick one" /></SelectTrigger>
                    <SelectContent>{(currentType?.units || []).map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">From</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-2 bg-[#F5EFE6] h-11 rounded-lg" data-testid="block-start" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">To</Label>
                  <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-2 bg-[#F5EFE6] h-11 rounded-lg" data-testid="block-end" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Reason</Label>
                  <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-2 bg-[#F5EFE6] h-11 rounded-lg" placeholder="Optional" data-testid="block-reason" />
                </div>
              </div>
              <div className="mt-5 flex justify-end">
                <button type="submit" className="btn-primary font-brand tracking-[0.14em]" data-testid="block-submit">
                  <Plus size={16} /> Add block
                </button>
              </div>
            </form>

            {/* Bulk block by category */}
            <div className="mt-6 bg-[#14100F] border border-[#2a1a1c] rounded-2xl p-6 md:p-8 text-[#F5EFE6]" data-testid="admin-bulk-block-card">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <p className="overline text-[#B76E79] flex items-center gap-2"><span className="marker-line marker-line--rose" /> Bulk close · open</p>
                <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#B76E79] text-[#14100F] font-semibold">Events</span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl mt-2 text-[#F5EFE6]">Close (or reopen) whole categories at once</h3>
              <p className="mt-2 text-sm text-[#F5EFE6]/70 max-w-xl">
                Use this for a private event, on-site works, or seasonal closure — every pitch or lodge in the selected category is blocked (or unblocked) for the date range in one click.
              </p>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">Category</Label>
                  <Select value={bulkForm.category} onValueChange={(v) => setBulkForm({ ...bulkForm, category: v })}>
                    <SelectTrigger className="mt-2 bg-[#0F0E0E] border-[#2a1a1c] text-[#F5EFE6] h-11 rounded-lg" data-testid="bulk-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hardstanding">All Hardstanding Pitches (15)</SelectItem>
                      <SelectItem value="grass">All Grass Pitches (12)</SelectItem>
                      <SelectItem value="tent">All Tent Pitches (20)</SelectItem>
                      <SelectItem value="lodge">All Lodges (3)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">From</Label>
                  <Input
                    type="date"
                    value={bulkForm.start_date}
                    onChange={(e) => setBulkForm({ ...bulkForm, start_date: e.target.value })}
                    className="mt-2 bg-[#0F0E0E] border-[#2a1a1c] text-[#F5EFE6] h-11 rounded-lg"
                    data-testid="bulk-start"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">To</Label>
                  <Input
                    type="date"
                    value={bulkForm.end_date}
                    onChange={(e) => setBulkForm({ ...bulkForm, end_date: e.target.value })}
                    className="mt-2 bg-[#0F0E0E] border-[#2a1a1c] text-[#F5EFE6] h-11 rounded-lg"
                    data-testid="bulk-end"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#F5EFE6]/70">Reason (visible internally)</Label>
                  <Input
                    value={bulkForm.reason}
                    onChange={(e) => setBulkForm({ ...bulkForm, reason: e.target.value })}
                    className="mt-2 bg-[#0F0E0E] border-[#2a1a1c] text-[#F5EFE6] h-11 rounded-lg"
                    placeholder="e.g. Wedding hire · Owner event"
                    data-testid="bulk-reason"
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => submitBulk("clear")}
                  disabled={bulkBusy}
                  className="inline-flex items-center gap-2 rounded-full px-5 h-10 border border-[#F5EFE6]/25 text-[#F5EFE6] hover:bg-[#F5EFE6] hover:text-[#14100F] font-brand text-xs uppercase tracking-[0.14em] transition-colors disabled:opacity-50"
                  data-testid="bulk-clear"
                >
                  {bulkBusy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  Open dates (clear blocks)
                </button>
                <button
                  type="button"
                  onClick={() => submitBulk("block")}
                  disabled={bulkBusy}
                  className="inline-flex items-center gap-2 rounded-full px-5 h-10 bg-gradient-to-r from-[#B76E79] to-[#8C4E58] text-[#F5EFE6] font-brand text-xs uppercase tracking-[0.14em] hover:from-[#C48690] hover:to-[#A75E68] transition-colors disabled:opacity-50"
                  data-testid="bulk-block"
                >
                  {bulkBusy ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />}
                  Block whole category
                </button>
              </div>
            </div>

            {/* Multi-select individual pitches */}
            <div className="mt-6 bg-white border border-[#DDD1BE] rounded-2xl p-6 md:p-8 shadow-sm" data-testid="admin-multi-select-card">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <p className="overline text-[#B76E79] flex items-center gap-2"><span className="marker-line marker-line--rose" /> Individual pitches</p>
                <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#0F0E0E] text-[#F5EFE6] font-semibold">Cherry-pick</span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl mt-2 text-[#14100F]">Select specific pitches to close or reopen</h3>
              <p className="mt-2 text-sm text-[#6A5F58] max-w-2xl">
                Tap any pitch tile to toggle it. Use the group buttons to quickly toggle whole rows. Then choose dates and either block or open the selection in one click. Attach an internal voice note (auto-transcribed) if you need to remember why.
              </p>

              {/* Pitch grid */}
              <div className="mt-6 space-y-5">
                {PITCH_GROUPS.map((grp) => {
                  const groupOn = grp.units.every((u) => multiSel[`${u.unit_type}:${u.unit_id}`]);
                  return (
                    <div key={grp.key} data-testid={`multi-group-${grp.key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#6A5F58] font-semibold">{grp.label}</p>
                        <button
                          type="button"
                          onClick={() => setMultiGroup(grp.units)}
                          className="text-[11px] uppercase tracking-[0.14em] text-[#B76E79] hover:text-[#8C4E58]"
                          data-testid={`multi-group-toggle-${grp.key}`}
                        >
                          {groupOn ? "Deselect all" : "Select all"}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {grp.units.map((u) => {
                          const key = `${u.unit_type}:${u.unit_id}`;
                          const on = !!multiSel[key];
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => toggleMulti(u.unit_type, u.unit_id)}
                              className={`min-w-[54px] px-2.5 h-10 rounded-lg border font-mono text-xs transition-all ${
                                on
                                  ? "bg-[#B76E79] text-white border-[#B76E79] shadow-[0_4px_12px_-6px_rgba(183,110,121,0.5)]"
                                  : "bg-[#F5EFE6] text-[#14100F] border-[#DDD1BE] hover:border-[#B76E79] hover:text-[#B76E79]"
                              }`}
                              data-testid={`multi-pitch-${u.unit_id}`}
                            >
                              {u.short}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {selectedMultiUnits.length > 0 && (
                <div className="mt-5 flex items-center gap-3 text-xs">
                  <span className="inline-flex items-center gap-2 rounded-full bg-[#B76E79] text-white px-3 h-7 font-semibold" data-testid="multi-selected-count">
                    {selectedMultiUnits.length} selected
                  </span>
                  <button
                    type="button"
                    onClick={clearMulti}
                    className="text-[#8C2E39] hover:underline uppercase tracking-[0.14em]"
                    data-testid="multi-clear-selection"
                  >
                    Clear selection
                  </button>
                </div>
              )}

              {/* Date + reason */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">From</Label>
                  <Input type="date" value={multiForm.start_date} onChange={(e) => setMultiForm({ ...multiForm, start_date: e.target.value })} className="mt-2 h-11 rounded-lg" data-testid="multi-start" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">To</Label>
                  <Input type="date" value={multiForm.end_date} onChange={(e) => setMultiForm({ ...multiForm, end_date: e.target.value })} className="mt-2 h-11 rounded-lg" data-testid="multi-end" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">Reason (internal)</Label>
                  <Input value={multiForm.reason} onChange={(e) => setMultiForm({ ...multiForm, reason: e.target.value })} className="mt-2 h-11 rounded-lg" placeholder="e.g. Wedding hire" data-testid="multi-reason" />
                </div>
              </div>

              <div className="mt-5">
                <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58]">Voice note (optional · admin only)</Label>
                <div className="mt-2">
                  <VoiceNoteRecorder
                    value={multiNote}
                    onChange={setMultiNote}
                    placeholder="Record a voice note — auto-transcribed. Or type here directly."
                  />
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => submitMulti("clear")}
                  disabled={multiBusy || selectedMultiUnits.length === 0}
                  className="inline-flex items-center gap-2 rounded-full px-5 h-10 border border-[#0F0E0E] text-[#14100F] hover:bg-[#F5EFE6] font-brand text-xs uppercase tracking-[0.14em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="multi-clear"
                >
                  {multiBusy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  Open selected
                </button>
                <button
                  type="button"
                  onClick={() => submitMulti("block")}
                  disabled={multiBusy || selectedMultiUnits.length === 0}
                  className="inline-flex items-center gap-2 rounded-full px-5 h-10 bg-gradient-to-r from-[#B76E79] to-[#8C4E58] text-[#F5EFE6] font-brand text-xs uppercase tracking-[0.14em] hover:from-[#C48690] hover:to-[#A75E68] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="multi-block"
                >
                  {multiBusy ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />}
                  Block selected
                </button>
              </div>
            </div>

            <div className="mt-8 bg-white border border-[#DDD1BE] rounded-2xl overflow-hidden">
              <table className="w-full text-sm" data-testid="admin-blocks-table">
                <thead className="bg-[#F5EFE6] text-[#6A5F58] uppercase tracking-[0.18em] text-xs">
                  <tr>
                    <th className="text-left p-4">Unit</th>
                    <th className="text-left p-4">Dates</th>
                    <th className="text-left p-4">Reason</th>
                    <th className="p-4" />
                  </tr>
                </thead>
                <tbody>
                  {blocks.length === 0 && (<tr><td colSpan={4} className="p-8 text-center text-[#6A5F58]">No blocks in place.</td></tr>)}
                  {blocks.map((b) => (
                    <tr key={b.id} className="border-t border-[#DDD1BE]">
                      <td className="p-4 text-[#14100F]"><div>{b.unit_id}</div><div className="text-xs text-[#6A5F58]">{b.unit_type}</div></td>
                      <td className="p-4 text-[#14100F]">{b.start_date} → {b.end_date}</td>
                      <td className="p-4 text-[#6A5F58]">{b.reason || "—"}</td>
                      <td className="p-4 text-right">
                        <button onClick={() => removeBlock(b.id)} className="text-[#8C2E39] hover:text-[#5D1F27] p-2 rounded-full transition" data-testid={`block-delete-${b.id}`} aria-label="Remove block">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "content" && (
          <section>
            <p className="overline text-[#6A5F58]"><span className="marker-line" /> Site content</p>
            <h2 className="font-serif text-3xl md:text-4xl mt-4 text-[#14100F]">Edit every section</h2>
            <p className="mt-3 text-[#6A5F58] text-sm max-w-lg">
              Change any text or image on the public site. Leave a field blank to fall back to the default copy.
            </p>
            <div className="mt-8">
              <ContentEditor />
            </div>
          </section>
        )}

        {tab === "calendar" && <CalendarSync units={units} />}

        {tab === "reviews" && <AdminReviews />}
        {tab === "rewards" && <AdminRewards />}
        {tab === "pitch-media" && (
          <>
            <div className="mb-6">
              <AdminPitchMap />
            </div>
            <AdminPitchMedia />
          </>
        )}
        {tab === "arrivals" && <AdminArrivals />}
    {tab === "onsite" && <AdminOnSite />}
    {tab === "checkins" && <AdminCheckIns />}
        {tab === "analytics" && <AdminAnalytics />}

        {tab === "accounts" && <AdminAccounts />}
        {tab === "assistant" && <AdminAssistant />}
        {tab === "customers" && (
          <AdminCustomers
            onCreateManualBooking={(prefill) => {
              setManualForm((f) => ({
                ...f,
                guest_name: prefill.guest_name || "",
                guest_email: prefill.guest_email || "",
                guest_phone: prefill.guest_phone || "",
              }));
              setShowManualForm(true);
              setTab("bookings");
            }}
          />
        )}

        {tab === "settings" && (
          <section className="max-w-2xl space-y-6">
            <p className="overline text-[#6A5F58]"><span className="marker-line" /> Account</p>
            <h2 className="font-serif text-3xl md:text-4xl mt-4 text-[#14100F]">Settings</h2>

            <CalendarFeedCard />
            <DailyBriefCard />
            <VoiceBriefCard />
            <InstallCard />
            <NotificationsCard />
            <PasskeyCard />

            <div className="mt-8 bg-white border border-[#DDD1BE] rounded-2xl p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#0F0E0E] text-[#F5EFE6] flex items-center justify-center">
                  <KeyRound size={16} />
                </div>
                <div>
                  <div className="text-sm text-[#14100F]">Signed in as</div>
                  <div className="text-xs text-[#6A5F58]">{me?.email}</div>
                </div>
              </div>

              <form onSubmit={changePassword} className="mt-6 space-y-4" data-testid="change-password-form">
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Current password</Label>
                  <Input type="password" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} className="mt-2 bg-[#F5EFE6] h-11 rounded-lg" required data-testid="pw-current" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">New password</Label>
                  <Input type="password" value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} className="mt-2 bg-[#F5EFE6] h-11 rounded-lg" required minLength={8} data-testid="pw-next" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.18em] text-[#6A5F58] font-brand">Confirm new password</Label>
                  <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="mt-2 bg-[#F5EFE6] h-11 rounded-lg" required minLength={8} data-testid="pw-confirm" />
                </div>
                <button type="submit" className="btn-primary font-brand tracking-[0.14em] w-full justify-center" data-testid="pw-submit">
                  Update password
                </button>
              </form>
            </div>
          </section>
        )}
      </main>

      {payLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0F0E0E]/60 p-4" data-testid="payment-link-modal">
          <div className="bg-[#FBF7F1] border border-[#DDD1BE] rounded-2xl max-w-lg w-full p-6 md:p-8 shadow-2xl">
            <h3 className="font-serif text-2xl text-[#14100F]">Payment link ready</h3>
            <p className="mt-2 text-sm text-[#6A5F58] leading-relaxed">
              {payLink.amount != null && <strong className="text-[#14100F]">{gbp(payLink.amount)}</strong>}
              {payLink.guest ? ` for ${payLink.guest}` : ""}
              {payLink.emailed
                ? ` — also emailed to ${payLink.email}.`
                : " — the email didn't go out, so do pass this on yourself."}
              {" "}Copy the link to send it by text or WhatsApp:
            </p>
            {payLink.revived && (
              <p className="mt-2 text-[10px] uppercase tracking-[0.12em] font-semibold text-[#3B8C4E]" data-testid="payment-link-revived-note">
                ✓ Booking revived to pending · unit held for 60 minutes
              </p>
            )}
            <div className="mt-4 flex items-stretch gap-2">
              <input
                readOnly
                value={payLink.url || ""}
                onFocus={(e) => e.target.select()}
                className="flex-1 min-w-0 bg-white border border-[#DDD1BE] rounded-lg px-3 text-xs text-[#0F0E0E] font-mono"
                data-testid="payment-link-url"
              />
              <button
                type="button"
                onClick={copyPayLink}
                className="inline-flex items-center gap-1.5 px-4 rounded-lg bg-[#0F0E0E] text-[#F5EFE6] hover:bg-[#B76E79] transition text-[10px] font-brand tracking-[0.12em] uppercase"
                data-testid="payment-link-copy-btn"
              >
                <Copy size={13} /> Copy
              </button>
              {payLink.phone && (
                <a
                  href={smsHref(payLink.phone, `Hi ${firstName(payLink.guest)}, here's your Walnut Lakes payment link${payLink.amount != null ? ` for ${gbp(payLink.amount)}` : ""}: ${payLink.url}`)}
                  className="inline-flex items-center gap-1.5 px-4 rounded-lg bg-[#3B8C4E] text-white hover:bg-[#0F0E0E] transition text-[10px] font-brand tracking-[0.12em] uppercase"
                  data-testid="payment-link-text-btn"
                >
                  <MessageSquareText size={13} /> Text
                </a>
              )}
            </div>
            <button
              type="button"
              onClick={() => setPayLink(null)}
              className="mt-5 w-full py-2.5 rounded-full border border-[#DDD1BE] text-[#6A5F58] hover:bg-[#F5EFE6] transition text-[10px] font-brand tracking-[0.14em] uppercase"
              data-testid="payment-link-close-btn"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Two-step fail-safe for destructive booking actions — delete / cancel /
          refund all pass through this: summary first, then a red final confirm. */}
      {danger && (
        <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDanger(null)}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="danger-confirm-modal">
            <div className={`p-5 ${danger.step === 2 ? "bg-[#8C2E39]" : "bg-[#F5EFE6]"}`}>
              <p className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${danger.step === 2 ? "text-white/70" : "text-[#8C2E39]"}`}>
                {danger.kind === "delete" ? "Delete booking" : danger.kind === "cancel" ? "Cancel booking" : "Refund guest"} · step {danger.step} of 2
              </p>
              <h3 className={`font-heading text-xl mt-1 ${danger.step === 2 ? "text-white" : "text-[#14100F]"}`}>
                {danger.step === 2
                  ? "Are you absolutely sure?"
                  : danger.kind === "delete"
                    ? "Permanently delete this booking?"
                    : danger.kind === "cancel"
                      ? "Cancel this booking?"
                      : `Refund £${(danger.amount || 0).toFixed(2)} to the guest's card?`}
              </h3>
            </div>
            <div className="p-5">
              <div className="text-sm text-[#14100F] space-y-1" data-testid="danger-confirm-summary">
                <p className="font-semibold">{danger.booking.guest_name || danger.booking.guest_email || "Guest"}</p>
                <p className="text-[#6A5F58] text-xs">
                  {incompleteUnitLabel(danger.booking)} · {danger.booking.start_date} → {danger.booking.end_date} · £{(danger.booking.total || 0).toFixed(2)}
                </p>
                {danger.kind === "cancel" && danger.policy && (
                  <div className="mt-2 rounded-xl border border-[#DDD1BE] bg-[#F5EFE6] p-3" data-testid="danger-policy">
                    <p className="text-[10px] uppercase tracking-[0.14em] text-[#6A5F58] font-semibold">Cancellation policy applies</p>
                    <p className="text-xs text-[#14100F] mt-1 font-medium">{danger.policy.band_label}</p>
                    {danger.policy.refund > 0 ? (
                      <p className="text-sm font-bold text-[#8C2E39] mt-1">£{danger.policy.refund.toFixed(2)} refunded to the guest's card immediately</p>
                    ) : (
                      <p className="text-sm font-bold text-[#8C2E39] mt-1">£0.00 card refund — 12-month credit voucher</p>
                    )}
                  </div>
                )}
                {danger.kind === "refund" && (
                  <p className="text-xs text-[#8C2E39] font-medium">
                    The money goes straight back onto the guest's card — it cannot be pulled back.
                  </p>
                )}
                {danger.step === 2 && <p className="text-xs font-semibold text-[#8C2E39] pt-2">This cannot be undone.</p>}
              </div>
              <div className="flex gap-2 mt-5">
                {danger.step === 1 ? (
                  <>
                    <button
                      onClick={() => setDanger(null)}
                      className="flex-1 h-11 rounded-xl border border-[#DDD1BE] text-[#14100F] hover:bg-[#F5EFE6] transition text-[11px] uppercase tracking-[0.14em] font-semibold"
                      data-testid="danger-cancel-btn"
                    >
                      Keep as is
                    </button>
                    <button
                      onClick={() => setDanger({ ...danger, step: 2 })}
                      className="flex-1 h-11 rounded-xl bg-[#8C2E39] hover:bg-[#6d2230] text-white transition text-[11px] uppercase tracking-[0.14em] font-semibold"
                      data-testid="danger-continue-btn"
                    >
                      Continue →
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setDanger({ ...danger, step: 1 })}
                      className="flex-1 h-11 rounded-xl border border-[#DDD1BE] text-[#14100F] hover:bg-[#F5EFE6] transition text-[11px] uppercase tracking-[0.14em] font-semibold"
                      data-testid="danger-back-btn"
                    >
                      ← Back
                    </button>
                    <button
                      onClick={executeDanger}
                      className="flex-1 h-11 rounded-xl bg-[#8C2E39] hover:bg-[#6d2230] text-white transition text-[11px] uppercase tracking-[0.14em] font-semibold"
                      data-testid="danger-execute-btn"
                    >
                      Yes, {danger.kind === "delete" ? "delete it" : danger.kind === "cancel" ? "cancel it" : "refund now"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
